import React, { useCallback, useEffect, useMemo, useState } from 'react';
import pinterpolate from 'pinterpolate';
import { Redirect } from 'react-router-dom';

import { EMPLOYEE_PROFILE, UPDATE_EMPLOYEE } from 'constants/routes';

import initialFormData from 'constants/initialFormData';

import * as toast from 'utils/toast';
import { handleError } from 'utils/errorHandler';

import {
  useDepartmentsQuery,
  useDesignationAreasQuery,
  useDesignationsQuery,
  useEngagementStatusesQuery,
} from 'hooks/query/useLookupQueries';
import { useCreateUserMutation, useUploadUserImageMutation } from 'hooks/query/useUserQueries';
import { useRolesQuery } from 'hooks/query/useRoles';

import { useUserPermissions } from 'hooks/query/useUserInfo';
import { filterAllowedRoles } from 'utils/user';
import Loading from 'components/common/loading/Loading';
import EmployeeForm from '../components/employeeForm/EmployeeForm';

import type {
  AppraiserAllocation,
  CreateUpdateForm,
  LeapfrogHistory,
  ProfileInfo,
  UserPayloadInput,
} from 'interface/createUpdateForm';
import type { Role } from 'interface/role';
import type { EmployeeLike } from 'interface/userUtils';

interface RedirectTarget {
  pageUrl: string;
  params: {
    id: number;
  };
}

interface PermissionsMap {
  [key: string]: boolean;
}

interface ApiError extends Error {
  response?: {
    data?: {
      error?: {
        message?: string;
      };
    };
  };
}

type EmployeeFormValues = CreateUpdateForm & ProfileInfo & LeapfrogHistory & AppraiserAllocation;
type EmployeeFormInput = EmployeeFormValues | typeof initialFormData;
type EmployeeImageValue = Array<{ file?: File }> | File | null | undefined;

const CreateForm: React.FC = () => {
  const permissions = useUserPermissions() as PermissionsMap;
  const { data: designations = [], isLoading: isLoadingDesignations } = useDesignationsQuery();
  const { data: departments = [], isLoading: isLoadingDepartments } = useDepartmentsQuery();
  const { data: designationAreas = [], isLoading: isLoadingDesignationAreas } = useDesignationAreasQuery();
  const { data: engagementStatus = [], isLoading: isLoadingEngagementStatus } = useEngagementStatusesQuery();
  const { data: allRoles = [], isLoading: isLoadingRoles } = useRolesQuery();
  const createUserMutation = useCreateUserMutation();
  const uploadUserImageMutation = useUploadUserImageMutation();

  const [employees] = useState<EmployeeLike[]>([]);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [formData] = useState<EmployeeFormInput>(initialFormData);
  const [toPage, setToPage] = useState<RedirectTarget | null>(null);

  const roles = useMemo(
    () =>
      filterAllowedRoles(
        allRoles.map((role) => ({ id: String(role.id), name: role.name })),
        permissions
      ).map((item) => ({
        id: Number(item.id),
        name: typeof item.name === 'string' ? item.name : '',
      })) as Role[],
    [allRoles, permissions]
  );

  const isLoading =
    isLoadingDesignations ||
    isLoadingDepartments ||
    isLoadingDesignationAreas ||
    isLoadingEngagementStatus ||
    isLoadingRoles;

  const handleCreateClick = useCallback(
    async (submittedFormData: UserPayloadInput) => {
      setIsSubmitting(true);

      const { employeeImage: imageValue, ...restFormData } = submittedFormData as UserPayloadInput & {
        employeeImage?: EmployeeImageValue;
      };
      const createFormData = { ...restFormData };
      delete createFormData.empId;
      const employeeImageInput = imageValue as EmployeeImageValue;
      let employeeImage: File | null = null;
      if (employeeImageInput instanceof File) {
        employeeImage = employeeImageInput;
      } else if (Array.isArray(employeeImageInput)) {
        const firstImage = employeeImageInput[0];
        if (firstImage?.file instanceof File) {
          employeeImage = firstImage.file;
        }
      }

      try {
        const response = (await createUserMutation.mutateAsync({
          ...createFormData,
          middleName: createFormData.middleName || null,
        })) as { data: { id: number } };
        const createdUserId = response.data.id;

        if (createdUserId) {
          toast.success({
            title: 'Success',
            message: 'Employee has been created successfully.',
          });

          if (employeeImage) {
            try {
              const imageUploadFormData = new FormData();
              imageUploadFormData.append('employeeImage', employeeImage);

              await uploadUserImageMutation.mutateAsync({ userId: createdUserId, formData: imageUploadFormData });
              setToPage({ pageUrl: EMPLOYEE_PROFILE, params: { id: createdUserId } });
            } catch {
              setToPage({ pageUrl: UPDATE_EMPLOYEE, params: { id: createdUserId } });
            }
          } else {
            setToPage({ pageUrl: EMPLOYEE_PROFILE, params: { id: createdUserId } });
          }
        }

        setIsSubmitting(false);
      } catch (error) {
        const formattedError = error as ApiError;
        if (formattedError.response?.data?.error) {
          formattedError.response.data.error.message = 'Oops! Something went wrong';
        }
        handleError(formattedError);
        setIsSubmitting(false);
      }
    },
    [createUserMutation, uploadUserImageMutation]
  );

  useEffect(() => {
    window.document.title = 'Create new employee | Vyaguta';
  }, []);

  if (isLoading) {
    return (
      <div className="container loading-container">
        <Loading />
      </div>
    );
  }

  if (toPage) {
    const togo = pinterpolate(toPage.pageUrl, toPage.params);
    return <Redirect to={togo} />;
  }

  return (
    <div>
      <EmployeeForm
        formData={formData}
        isCreateForm={true}
        isSubmitting={isSubmitting}
        handleSubmit={handleCreateClick}
        employees={employees}
        canEdit={() => true}
        departments={departments}
        designations={designations}
        designationAreas={designationAreas}
        engagementStatus={engagementStatus}
        roles={roles}
        skills={[]}
      />
    </div>
  );
};

export default CreateForm;
